import { type ReactNode, useState, useEffect, useCallback } from "react";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Build as BuildIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import type { EquipmentTreeRowFlat, PartInfo } from "../../models";

interface Props<TRow extends EquipmentTreeRowFlat = EquipmentTreeRowFlat> {
  selectedRow: TRow | null;
  /** Optional tree data for building thumbnail context */
  treeData?: TRow[];
  onInstall: (row: TRow) => void;
  onUninstall: (row: TRow) => void;
  onRepair: (row: TRow) => void;
  onNewSparePart: (row: TRow) => void;
  isSpareContainer: (containerId: number | undefined) => boolean;
  /**
   * Optional render prop for equipment/location thumbnail (SVG, 3D model, etc.)
   * Receives the selected row and optional tree data for context
   */
  renderThumbnail?: (selectedRow: TRow, treeData?: TRow[]) => ReactNode;
  /** Whether to show Make/Model/Vendor fields. Defaults to true (EAF Roof/Sidewall). Set false for Caster. */
  showMakeModelVendor?: boolean;
  /** Whether to show Serial Number field. Defaults to true. Caster only shows for Copper Mold Tube. */
  showSerialNumber?: boolean;
  /** Whether to show Warehouse/Drawing Number fields. Defaults to false. Only Caster Mold uses these. */
  showWarehouseDrawing?: boolean;
  /** Callback to update part details on blur. When provided, spare part fields become editable. */
  onUpdatePartDetails?: (partId: number, partInfo: Partial<PartInfo>) => void;
}

export const DetailsPanel = <TRow extends EquipmentTreeRowFlat>({
  selectedRow,
  treeData,
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  isSpareContainer,
  renderThumbnail,
  showMakeModelVendor = true,
  showSerialNumber = true,
  showWarehouseDrawing = false,
  onUpdatePartDetails,
}: Props<TRow>) => {
  const isContainerSelected =
    selectedRow?.rowType === "container" ||
    selectedRow?.rowType === "sparesContainer";
  const isSpareContainerSelected =
    selectedRow?.rowType === "sparesContainer" &&
    isSpareContainer(selectedRow?.ContainerId);
  const isSparePartSelected = selectedRow?.rowType === "sparePart";
  const isLocationSelected = selectedRow?.rowType === "location";
  const isEquipmentSelected = selectedRow?.rowType === "equipment";
  const hasPart = selectedRow?.HasAssignedPart ?? false;
  // Positions with child sub-components (parts install on children, not parent)
  // Exception: Shells (PartTypeId 306) have children but should show their own details
  const SHELL_PART_TYPE_ID = 306;
  const isShell = selectedRow?.PartTypeId === SHELL_PART_TYPE_ID;
  const hasChildComponents = selectedRow?.HasChildren === true && !isShell;

  // Get part info values for read-only display
  const partInfo = selectedRow?.PartInfo;

  // Editable fields for spare parts (synced with selectedRow)
  const canEditDetails = isSparePartSelected && !!onUpdatePartDetails;
  const partId = selectedRow?.L2PartId;

  const [localSerialNumber, setLocalSerialNumber] = useState("");
  const [localWarehouseNumber, setLocalWarehouseNumber] = useState("");
  const [localDrawingNumber, setLocalDrawingNumber] = useState("");

  useEffect(() => {
    setLocalSerialNumber(partInfo?.SerialNumber ?? "");
    setLocalWarehouseNumber(partInfo?.WarehouseNumber ?? "");
    setLocalDrawingNumber(partInfo?.DrawingNumber ?? "");
  }, [
    partInfo?.SerialNumber,
    partInfo?.WarehouseNumber,
    partInfo?.DrawingNumber,
  ]);

  const handleBlur = useCallback(
    (field: keyof PartInfo, localValue: string, originalValue: string) => {
      if (!onUpdatePartDetails || !partId || localValue === originalValue)
        return;
      onUpdatePartDetails(partId, { [field]: localValue || null });
    },
    [onUpdatePartDetails, partId],
  );

  const getTitle = () => {
    if (!selectedRow) return "Details";
    if (
      selectedRow.rowType === "container" ||
      selectedRow.rowType === "sparesContainer"
    ) {
      return `Details - ${selectedRow.ContainerName ?? ""}`;
    }
    if (selectedRow.rowType === "sparePart") {
      return `Details - ${selectedRow.L2PartName ?? ""}`;
    }
    if (selectedRow.rowType === "equipment") {
      return `Details - ${selectedRow.EquipmentName}`;
    }
    return `Details - ${selectedRow.EquipmentName ?? ""} / ${selectedRow.PositionName ?? ""}`;
  };

  return (
    <Paper
      elevation={2}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {getTitle()}
        </Typography>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            p: 1,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
            overflow: "auto",
          }}
        >
          {isSpareContainerSelected ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Select a part or create a new one
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => selectedRow && onNewSparePart(selectedRow)}
              >
                New Spare Part
              </Button>
            </Box>
          ) : isContainerSelected ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              Please Select a Part
            </Typography>
          ) : isEquipmentSelected ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: 1,
              }}
            >
              {renderThumbnail && selectedRow ? (
                renderThumbnail(selectedRow, treeData)
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedRow?.EquipmentName}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  py: 0.5,
                  color:
                    selectedRow?.Status === "Online"
                      ? "success.dark"
                      : "text.secondary",
                }}
              >
                {selectedRow?.Status === "Online" ? (
                  <CheckCircleIcon sx={{ fontSize: "1.25rem" }} />
                ) : (
                  <CancelIcon sx={{ fontSize: "1.25rem" }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.9rem",
                    letterSpacing: "0.05em",
                  }}
                >
                  {selectedRow?.Status === "Online" ? "ONLINE" : "OFFLINE"}
                </Typography>
              </Box>
            </Box>
          ) : isSparePartSelected ? (
            // Spare part selected - show part info (editable when onUpdatePartDetails provided)
            <Grid container spacing={1}>
              {showSerialNumber ? (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Serial Number"
                    value={
                      canEditDetails
                        ? localSerialNumber
                        : (partInfo?.SerialNumber ?? "")
                    }
                    onChange={
                      canEditDetails
                        ? (e) => setLocalSerialNumber(e.target.value)
                        : undefined
                    }
                    onBlur={
                      canEditDetails
                        ? () =>
                            handleBlur(
                              "SerialNumber",
                              localSerialNumber,
                              partInfo?.SerialNumber ?? "",
                            )
                        : undefined
                    }
                    disabled={!canEditDetails}
                  />
                </Grid>
              ) : null}
              {showMakeModelVendor ? (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Make"
                      value={partInfo?.Make ?? ""}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Model"
                      value={partInfo?.Model ?? ""}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Vendor"
                      value={partInfo?.Vendor ?? ""}
                      disabled
                    />
                  </Grid>
                </>
              ) : null}
              {showWarehouseDrawing ? (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Warehouse Number"
                      value={
                        canEditDetails
                          ? localWarehouseNumber
                          : (partInfo?.WarehouseNumber ?? "")
                      }
                      onChange={
                        canEditDetails
                          ? (e) => setLocalWarehouseNumber(e.target.value)
                          : undefined
                      }
                      onBlur={
                        canEditDetails
                          ? () =>
                              handleBlur(
                                "WarehouseNumber",
                                localWarehouseNumber,
                                partInfo?.WarehouseNumber ?? "",
                              )
                          : undefined
                      }
                      disabled={!canEditDetails}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Drawing Number"
                      value={
                        canEditDetails
                          ? localDrawingNumber
                          : (partInfo?.DrawingNumber ?? "")
                      }
                      onChange={
                        canEditDetails
                          ? (e) => setLocalDrawingNumber(e.target.value)
                          : undefined
                      }
                      onBlur={
                        canEditDetails
                          ? () =>
                              handleBlur(
                                "DrawingNumber",
                                localDrawingNumber,
                                partInfo?.DrawingNumber ?? "",
                              )
                          : undefined
                      }
                      disabled={!canEditDetails}
                    />
                  </Grid>
                </>
              ) : null}
            </Grid>
          ) : isLocationSelected && hasChildComponents ? (
            // Position has child sub-components - user must select a child to install parts
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: 2,
              }}
            >
              {renderThumbnail && selectedRow
                ? renderThumbnail(selectedRow, treeData)
                : null}
              <Typography variant="body1" color="text.secondary">
                Please select a Child Part
              </Typography>
            </Box>
          ) : isLocationSelected && !hasPart ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: 2,
              }}
            >
              {renderThumbnail && selectedRow
                ? renderThumbnail(selectedRow, treeData)
                : null}
              {!renderThumbnail ? (
                <Typography variant="body1" color="text.secondary">
                  No part installed at this position
                </Typography>
              ) : null}
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<LinkIcon />}
                onClick={() => selectedRow && onInstall(selectedRow)}
              >
                Install
              </Button>
            </Box>
          ) : isLocationSelected && hasPart ? (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                height: "100%",
                flexDirection: renderThumbnail ? "row" : "column",
              }}
            >
              {/* Left side: Thumbnail with action buttons (if renderThumbnail provided) */}
              {renderThumbnail && selectedRow ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  {renderThumbnail(selectedRow, treeData)}
                  {selectedRow?.rowType === "location" ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<LinkOffIcon />}
                        onClick={() => selectedRow && onUninstall(selectedRow)}
                      >
                        Uninstall
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        startIcon={<BuildIcon />}
                        onClick={() => selectedRow && onRepair(selectedRow)}
                      >
                        Repair
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
              {/* Right side (or full width if no thumbnail): Part Info Fields */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Grid container spacing={1}>
                  {showSerialNumber ? (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Serial Number"
                        value={partInfo?.SerialNumber ?? ""}
                        disabled
                      />
                    </Grid>
                  ) : null}
                  {showMakeModelVendor ? (
                    <>
                      <Grid item xs={renderThumbnail ? 12 : 6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Make"
                          value={partInfo?.Make ?? ""}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={renderThumbnail ? 12 : 6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Model"
                          value={partInfo?.Model ?? ""}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Vendor"
                          value={partInfo?.Vendor ?? ""}
                          disabled
                        />
                      </Grid>
                    </>
                  ) : null}
                  {showWarehouseDrawing ? (
                    <>
                      <Grid item xs={renderThumbnail ? 12 : 6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Warehouse Number"
                          value={partInfo?.WarehouseNumber ?? ""}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={renderThumbnail ? 12 : 6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Drawing Number"
                          value={partInfo?.DrawingNumber ?? ""}
                          disabled
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>

                {/* Action Buttons (only if no thumbnail - buttons are with thumbnail otherwise) */}
                {!renderThumbnail && selectedRow?.rowType === "location" ? (
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<LinkOffIcon />}
                      onClick={() => selectedRow && onUninstall(selectedRow)}
                    >
                      Uninstall
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<BuildIcon />}
                      onClick={() => selectedRow && onRepair(selectedRow)}
                    >
                      Repair
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              Select a location to view details
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
